interface StreamRecoveryPollInput {
  canonicalMessageId: string | null;
  activeRequestToken?: string;
  expectedRequestToken: string;
  lastActivityAt?: number;
  now: number;
  inactivityTimeoutMs: number;
}

export const shouldPollAfterStreamInactivity = ({
  canonicalMessageId,
  activeRequestToken,
  expectedRequestToken,
  lastActivityAt,
  now,
  inactivityTimeoutMs,
}: StreamRecoveryPollInput): boolean =>
  Boolean(canonicalMessageId) &&
  activeRequestToken === expectedRequestToken &&
  typeof lastActivityAt === "number" &&
  now - lastActivityAt >= inactivityTimeoutMs;

export type RequestedGenerationState = "active" | "terminal" | "invalid";

export const classifyRequestedGeneration = (
  requestedGenerationId: string,
  status: { message_id?: unknown; processing_status?: unknown }
): RequestedGenerationState => {
  if (status.message_id !== requestedGenerationId) return "invalid";
  if (
    status.processing_status === "pending" ||
    status.processing_status === "processing"
  ) {
    return "active";
  }
  if (
    status.processing_status === "complete" ||
    status.processing_status === "error"
  ) {
    return "terminal";
  }
  return "invalid";
};

export const shouldRetryGenerationAcceptance = ({
  responseStatus,
  now,
  deadline,
}: {
  responseStatus: number | null;
  now: number;
  deadline: number;
}): boolean => {
  if (now >= deadline) return false;
  return (
    responseStatus === null ||
    responseStatus === 404 ||
    responseStatus === 408 ||
    responseStatus === 429 ||
    responseStatus >= 500
  );
};

export const isDefinitiveGenerationCapacityRejection = (
  responseStatus: number,
  responseCode: unknown
): boolean =>
  responseStatus === 503 && responseCode === "generation_capacity";

export const hasProcessingRecoveryExpired = ({
  startedAt,
  now,
  timeoutMs,
}: {
  startedAt: number;
  now: number;
  timeoutMs: number;
}): boolean => now - startedAt >= timeoutMs;

export const runWithAbortTimeout = async <T>(
  timeoutMs: number,
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMessage: string
): Promise<T> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new DOMException(timeoutMessage, "AbortError"));
      controller.abort();
    }, Math.max(1, timeoutMs));
  });

  try {
    return await Promise.race([operation(controller.signal), deadline]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
};

export interface UnresolvedGenerationAttempt {
  userId: string;
  threadId: string;
  promptDigest: string;
  generationId: string;
  createdAt: string;
}

export const generationAttemptKey = (
  userId: string,
  threadId: string,
  promptDigest: string
): string => JSON.stringify([userId, threadId, promptDigest]);

export const generationPromptDigest = async (prompt: string): Promise<string> => {
  const bytes = new TextEncoder().encode(prompt);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
};

export const requestedGenerationIdForAttempt = (
  unresolvedAttempt: UnresolvedGenerationAttempt | undefined,
  userId: string,
  threadId: string,
  promptDigest: string,
  createGenerationId: () => string
): string =>
  unresolvedAttempt?.userId === userId &&
  unresolvedAttempt?.threadId === threadId &&
  unresolvedAttempt.promptDigest === promptDigest
    ? unresolvedAttempt.generationId
    : createGenerationId();
